import { useEffect, useMemo, useRef, useState } from "react";
import { Play, ExternalLink, AlertTriangle, Copy, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import MuxPlayer from "@/components/video/MuxPlayer";

interface Props {
  videoId: string;
  title: string;
  className?: string;
  /** Milliseconds to wait for the iframe to fire `load` before assuming it's blocked. */
  loadTimeoutMs?: number;
  /**
   * Optional callback fired when the user clicks "Retry" on the blocked
   * fallback. Parents can use this to bump a `key` and remount the embed.
   */
  onRetry?: () => void;
}

/**
 * YouTube embed that auto-detects when the iframe is blocked
 * (CSP, browser extension, owner restriction, network policy) and
 * surfaces a "Watch on YouTube" CTA without manual refresh.
 *
 * Detection strategy:
 *  1. Listen for `load` event on the iframe — fires for both successful
 *     embeds AND error pages, but never fires when the response is blocked
 *     by the browser (ERR_BLOCKED_BY_RESPONSE, X-Frame-Options refusal).
 *  2. If `load` does not fire within `loadTimeoutMs`, treat as blocked.
 *  3. Listen to `window.onmessage` for YouTube's iframe API postMessage
 *     handshake; receiving any message confirms a healthy embed.
 */
const YouTubeEmbed = ({ videoId, title, className, loadTimeoutMs = 4500, onRetry }: Props) => {
  const [state, setState] = useState<"loading" | "ready" | "blocked">("loading");
  const [errorType, setErrorType] = useState<"oembed_failed" | "load_timeout" | "iframe_error" | "unknown">("unknown");
  const [activeCta, setActiveCta] = useState<"open_watch_primary" | "copy_link_primary" | "dual_cta">("dual_cta");
  const [muxPlaybackId, setMuxPlaybackId] = useState<string | null>(null);
  const correlationIdRef = useRef<string>(crypto.randomUUID());
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timerRef = useRef<number | null>(null);
  const loggedRef = useRef(false);
  const watchUrl = useMemo(() => `https://www.youtube.com/watch?v=${videoId}`, [videoId]);
  const shortUrl = useMemo(() => `https://youtu.be/${videoId}`, [videoId]);

  // Look up a Mux mirror for this YouTube video (auto-fallback when blocked).
  useEffect(() => {
    let cancelled = false;
    setMuxPlaybackId(null);
    supabase
      .from("youtube_mux_mirrors")
      .select("mux_playback_id, mirror_status")
      .eq("youtube_video_id", videoId)
      .maybeSingle()
      .then(({ data }) => {
        if (
          !cancelled &&
          data?.mux_playback_id &&
          (data as any).mirror_status === "ready"
        ) {
          setMuxPlaybackId(data.mux_playback_id);
        }
      });
    return () => { cancelled = true; };
  }, [videoId]);

  useEffect(() => {
    setState("loading");
    loggedRef.current = false;
    correlationIdRef.current = crypto.randomUUID();
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setState((s) => {
        if (s === "loading") {
          setErrorType("load_timeout");
          return "blocked";
        }
        return s;
      });
    }, loadTimeoutMs);

    // Pre-flight: hit the public oEmbed endpoint to detect owner-restricted /
    // private / removed videos BEFORE waiting on the iframe load timeout.
    // If oEmbed returns 401/403/404 we know the iframe will refuse with
    // ERR_BLOCKED_BY_RESPONSE and we can surface the fallback immediately.
    let cancelled = false;
    fetch(`https://www.youtube.com/oembed?url=https%3A//www.youtube.com/watch%3Fv%3D${videoId}&format=json`, {
      mode: "no-cors",
      // no-cors hides the response but a network failure still rejects the promise.
    })
      .then(() => {
        // success path — iframe should load; let postMessage handler confirm
      })
      .catch(() => {
        if (!cancelled) {
          setErrorType("oembed_failed");
          setState("blocked");
        }
      });

    const onMessage = (e: MessageEvent) => {
      // YouTube iframe API postMessages originate from these origins.
      if (typeof e.origin === "string" && /youtube(-nocookie)?\.com$/.test(new URL(e.origin).hostname)) {
        setState("ready");
        if (timerRef.current) window.clearTimeout(timerRef.current);
      }
    };
    window.addEventListener("message", onMessage);

    return () => {
      cancelled = true;
      window.removeEventListener("message", onMessage);
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [videoId, loadTimeoutMs]);

  // Log blocked embeds (analytics) — fire once per (videoId, route)
  useEffect(() => {
    if (state !== "blocked" || loggedRef.current) return;
    loggedRef.current = true;
    supabase.from("youtube_embed_blocks").insert({
      route: window.location.pathname,
      video_id: videoId,
      user_agent: navigator.userAgent.slice(0, 250),
      error_type: errorType,
      active_cta_variant: activeCta,
      correlation_id: correlationIdRef.current,
    }).then(() => {}, () => {});
  }, [state, videoId, errorType, activeCta]);

  const recordFallback = (cta: "open_watch" | "copy_link") => {
    setActiveCta(cta === "open_watch" ? "open_watch_primary" : "copy_link_primary");
    supabase.from("youtube_embed_blocks").insert({
      route: window.location.pathname,
      video_id: videoId,
      fallback_cta: cta,
      user_agent: navigator.userAgent.slice(0, 250),
      error_type: errorType,
      active_cta_variant: cta === "open_watch" ? "open_watch_primary" : "copy_link_primary",
      correlation_id: correlationIdRef.current,
    }).then(() => {}, () => {});
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    // Self-reset when no parent handler is provided: re-trigger the load cycle.
    setState("loading");
    setErrorType("unknown");
    loggedRef.current = false;
    correlationIdRef.current = crypto.randomUUID();
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (iframeRef.current) {
      // Force the iframe to re-fetch.
      const src = iframeRef.current.src;
      iframeRef.current.src = "about:blank";
      window.setTimeout(() => {
        if (iframeRef.current) iframeRef.current.src = src;
      }, 50);
    }
    timerRef.current = window.setTimeout(() => {
      setState((s) => (s === "loading" ? "blocked" : s));
    }, loadTimeoutMs);
  };

  const handleLoad = () => {
    // `load` fires for blocked responses too in some browsers — keep timer
    // unless we also receive a postMessage. Mark as ready optimistically
    // and let postMessage confirm. If still no message after timeout,
    // user can still click the fallback CTA which is always visible below.
    setState((s) => (s === "loading" ? "ready" : s));
  };

  return (
    <div className={`relative ${className || ""}`}>
      {/* Auto-fallback: if blocked AND a Mux mirror exists, swap to MuxPlayer */}
      {state === "blocked" && muxPlaybackId && (
        <div className="absolute inset-0 z-30 bg-background">
          <div className="absolute top-2 left-2 z-40 px-2 py-1 rounded bg-sovereign-gold/20 backdrop-blur text-[9px] font-mono uppercase text-sovereign-gold border border-sovereign-gold/30">
            Mux mirror · YouTube blocked
          </div>
          <MuxPlayer playbackId={muxPlaybackId} autoPlay aspectRatio="" className="w-full h-full" />
        </div>
      )}
      {/* Skeleton / blocked overlay */}
      {state !== "ready" && !(state === "blocked" && muxPlaybackId) && (
        <div
          data-testid="youtube-embed-fallback"
          data-error-type={errorType}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/95 text-center p-6"
        >
          {state === "loading" ? (
            <>
              <div className="w-10 h-10 rounded-full border-2 border-sovereign-gold/30 border-t-sovereign-gold animate-spin" />
              <p className="text-[10px] font-mono text-muted-foreground">Loading YouTube player…</p>
            </>
          ) : (
            <>
              <AlertTriangle className="w-6 h-6 text-tactical-amber" />
              <p className="text-xs font-bold">This video can't be embedded here</p>
              <p className="text-[10px] text-muted-foreground max-w-xs">
                {errorType === "oembed_failed"
                  ? "YouTube returned an error for this video — it may be private, age-restricted, or owner-restricted from embedding."
                  : errorType === "load_timeout"
                  ? "The player didn't load in time. Your network, an extension, or a CSP rule may be blocking youtube.com."
                  : "We couldn't render the inline player for this video. You can still watch it on YouTube."}
              </p>
              <p className="text-[9px] font-mono text-muted-foreground/70">
                ref · {correlationIdRef.current.slice(0, 8)}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <a
                  href={watchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => recordFallback("open_watch")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sovereign-gold text-background font-bold text-sm hover:scale-105 transition-transform"
                >
                  <Play className="w-4 h-4" /> Open watch page <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(shortUrl); recordFallback("copy_link"); }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background text-foreground font-bold text-sm hover:bg-secondary/50 transition-colors"
                >
                  <Copy className="w-4 h-4" /> Copy link
                </button>
                <button
                  type="button"
                  data-testid="youtube-embed-retry"
                  onClick={handleRetry}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background text-foreground font-bold text-sm hover:bg-secondary/50 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" /> Retry
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&controls=1&rel=0&modestbranding=1&enablejsapi=1&playsinline=1`}
        className="w-full h-full relative z-0 bg-background"
        style={{ border: "none" }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        loading="lazy"
        title={title}
        onLoad={handleLoad}
        tabIndex={state === "ready" ? 0 : -1}
        aria-hidden={state !== "ready"}
      />
    </div>
  );
};

export default YouTubeEmbed;